import { useEffect, useRef, useState, type ReactNode } from 'react';
import CircularProgress from '@mui/material/CircularProgress';
import LinearProgress from '@mui/material/LinearProgress';
import Skeleton from '@mui/material/Skeleton';
import {
  useCharacterHexa,
  useDeleteHexaNode,
  useDeleteHexaStatNode,
  useUpsertHexaNode,
  useUpsertHexaStatNode,
  type HexaNodeDto,
  type HexaStatNodeDto,
} from '../../lib/characterProgress';
import { hexaRollup, type HexaRollup } from '../../lib/hexaOverview';
import { useHexaClassSkills, type HexaClassSkillDto } from '../../lib/reference';
import { CollapsibleSection } from './CollapsibleSection';
import { ConfirmDialog } from '../Modal';
import { commonCoreSlug } from '../hexa/commonCore';
import { EstimatePrefix } from '../hexa/EstimatePrefix';
import { HexaCurrencyIcon } from '../hexa/HexaCurrencyIcon';
import { HexaSkillTooltip } from '../hexa/HexaSkillTooltip';
import { HexaTypeIcon } from '../hexa/HexaTypeIcon';
import { IconTrash } from '../icons';

/**
 * Node inventory (mirrors `GoalsService`'s HEXA rules): origin/ascent are singletons, boost is a
 * fixed x4, common_core is the fixed Sol Janus (index 0) / Sol Hecate (index 1) pair — these four
 * always render every index the server sends. mastery and common_boost are flexible-count (the
 * per-class node count is unstated in research 09): the server only returns rows the player has
 * added, capped at 4 (index 0-3), so those two get an "add node" affordance here.
 */
const NODE_TYPE_ORDER = ['origin', 'ascent', 'boost', 'mastery', 'common_core', 'common_boost'] as const;
const FLEXIBLE_NODE_TYPES = new Set(['mastery', 'common_boost']);
const MAX_FLEXIBLE_NODES = 4;

const NODE_TYPE_LABEL: Record<string, string> = {
  origin: 'Origin',
  ascent: 'Ascent',
  boost: 'Boost',
  mastery: 'Mastery',
  common_core: 'Common Core (Sol Janus / Sol Hecate)',
  common_boost: '5th Job Common Boost',
};

/** Strips a leading "HEXA " tag off a skill name for the merged-label join form (REV22 Fix 3) —
 *  keeps bracket tags like "[Shinsoku]" intact, only drops the generic job-tier prefix. */
function stripHexaPrefix(name: string): string {
  return name.replace(/^HEXA /, '');
}

/**
 * REV21 — the resolved real skill name always wins when the server has one (every fixed type once
 * its class's `hexa_class_skill` catalog resolves it, or Common Boost once its single row
 * resolves). Falls back to the old generic label only when unresolved: `common_core` (never
 * resolves — stays the hardcoded Sol Janus/Sol Hecate labeling by design) or an uncataloged class's
 * flexible node.
 *
 * REV22 Fix 3 (docs/specs/REV22-hexa-stat-and-merged-masteries.md): a Mastery Core upgrades ALL its
 * catalog skills at once, so a merged slot's label spells that out — 2 skills join with " / "
 * (stripping each name's leading "HEXA " for a shorter joined form), 3+ skills read as the primary
 * name plus a "+N skills" count (Hoyoung's 11-skill core must not blow up the card).
 */
function nodeLabel(node: HexaNodeDto): string {
  if (node.skills.length === 2) {
    return node.skills.map((s) => stripHexaPrefix(s.skillName)).join(' / ');
  }
  if (node.skills.length >= 3) {
    return `${node.skills[0]!.skillName} +${node.skills.length - 1} skills`;
  }
  if (node.skillName) {
    return node.skillName;
  }
  switch (node.nodeType) {
    case 'origin':
      return 'Origin';
    case 'ascent':
      return 'Ascent';
    case 'boost':
      return `Boost ${node.nodeIndex + 1}`;
    case 'mastery':
      return `Mastery ${node.nodeIndex + 1}`;
    case 'common_core':
      return node.nodeIndex === 0 ? 'Sol Janus' : 'Sol Hecate';
    case 'common_boost':
      return `Common Boost ${node.nodeIndex + 1}`;
    default:
      return `${node.nodeType} ${node.nodeIndex + 1}`;
  }
}

/** REV22 Fix 1 (docs/specs/REV22-hexa-stat-and-merged-masteries.md): the icon slug for a node with
 *  no per-skill list (`common_core`, or an uncataloged class's flexible node) — `common_core` needs
 *  its per-index Sol Janus/Sol Hecate art instead of the single shared type icon it used to fall
 *  back to. Nodes with a resolved `skills` list are handled separately by `HexaNodeIcons` below. */
function fallbackIconSlug(node: HexaNodeDto): string | null {
  if (node.skillSlug) return node.skillSlug;
  if (node.nodeType === 'common_core') return commonCoreSlug(node.nodeIndex);
  return null;
}

/**
 * A node's icon(s). REV22 Fix 3: a resolved node (`skills.length > 0` — every fixed type, Common
 * Boost, and now every cataloged Mastery slot) renders one small icon per skill, each with its own
 * tooltip (`HexaSkillTooltip`'s `slugOverride`), capped at 3 with a "+N" count beyond that so
 * Hoyoung's 11-skill core doesn't blow up the row. An unresolved node (`common_core`, or an
 * uncataloged class's flexible node) falls back to the single generic-or-fallback icon it always had.
 */
function HexaNodeIcons({ node, label }: { node: HexaNodeDto; label: string }) {
  if (node.skills.length === 0) {
    return (
      <HexaSkillTooltip node={node} label={label}>
        <HexaTypeIcon nodeType={node.nodeType} skillSlug={fallbackIconSlug(node)} />
      </HexaSkillTooltip>
    );
  }
  const shown = node.skills.slice(0, 3);
  const extra = node.skills.length - shown.length;
  return (
    <span className="flex items-center gap-0.5">
      {shown.map((skill) => (
        <HexaSkillTooltip key={skill.skillSlug} node={node} label={skill.skillName} slugOverride={skill.skillSlug}>
          <HexaTypeIcon nodeType={node.nodeType} skillSlug={skill.skillSlug} />
        </HexaSkillTooltip>
      ))}
      {extra > 0 && (
        <span aria-hidden="true" className="text-[0.625rem] font-semibold text-text-faint">
          +{extra}
        </span>
      )}
    </span>
  );
}

/**
 * A 0-`max` number input that debounces its commit ~400ms after typing stops, so the bar refreshes
 * without needing blur/tab-away — a bare `onChange`-triggered commit would send a request for every
 * intermediate digit (e.g. "1" while typing "15"), each one a real network round trip since there's
 * no local value to preview. Not optimistic: the caller's `value` (used/missing SE+fragments come
 * from a server-side curve lookup) lags behind until the round trip + refetch lands, so this tracks
 * the last value it actually sent (rather than comparing against `value` directly) to avoid
 * re-sending the same number twice while a commit is still in flight. Shared by `HexaNodeRow`'s
 * level/target inputs (`max` 30) and `HexaStatCard`'s per-stat-level (`max` 10) / goal (`max` 20)
 * inputs (REV22 Fix 2).
 */
function useDebouncedLevelInput(value: number, onCommit: (level: number) => void, max = 30) {
  const [draft, setDraft] = useState(String(value));
  const lastCommittedRef = useRef(value);

  useEffect(() => {
    setDraft(String(value));
    lastCommittedRef.current = value;
  }, [value]);

  const commit = () => {
    const parsed = Number(draft);
    const clamped = Number.isFinite(parsed) ? Math.min(max, Math.max(0, Math.floor(parsed))) : lastCommittedRef.current;
    if (clamped !== lastCommittedRef.current) {
      lastCommittedRef.current = clamped;
      onCommit(clamped);
    } else {
      setDraft(String(lastCommittedRef.current));
    }
  };

  useEffect(() => {
    const timeout = setTimeout(commit, 400);
    return () => clearTimeout(timeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only re-arm when the typed draft changes
  }, [draft]);

  return [draft, setDraft, commit] as const;
}

function HexaNodeRow({
  characterId,
  node,
  removable,
  onLevelChange,
  onTargetChange,
}: {
  characterId: number;
  node: HexaNodeDto;
  removable: boolean;
  onLevelChange: (level: number) => void;
  onTargetChange: (targetLevel: number) => void;
}) {
  const [draft, setDraft, commit] = useDebouncedLevelInput(node.level, onLevelChange);
  const [targetDraft, setTargetDraft, commitTarget] = useDebouncedLevelInput(node.targetLevel, onTargetChange);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const deleteNode = useDeleteHexaNode(characterId);
  const label = nodeLabel(node);
  const inputId = `hexa-level-${node.nodeType}-${node.nodeIndex}`;
  const targetInputId = `hexa-target-${node.nodeType}-${node.nodeIndex}`;

  const missingSolErda = node.totalSolErda - node.usedSolErda;
  const missingFragments = node.totalFragments - node.usedFragments;

  const requestRemove = () => {
    if (node.level > 0) {
      setConfirmingDelete(true);
    } else {
      deleteNode.mutate({ nodeType: node.nodeType, nodeIndex: node.nodeIndex });
    }
  };

  return (
    <li className="rounded-lg border border-border bg-bg-elevated-hover p-3">
      <div className="flex items-center justify-between gap-2">
        <p className="flex items-center gap-1.5 text-sm font-medium text-text">
          <HexaNodeIcons node={node} label={label} />
          {label}
          {!node.verified && (
            <span
              className="ml-2 cursor-help text-[0.625rem] font-semibold uppercase tracking-wider text-warning underline decoration-dotted decoration-warning/70 underline-offset-2"
              title="This value comes from research that hasn't been confirmed in-game yet."
            >
              Unverified
            </span>
          )}
        </p>
        {removable && (
          <button
            type="button"
            aria-label={`Remove ${label}`}
            disabled={deleteNode.isPending}
            onClick={requestRemove}
            className="flex min-h-tap min-w-tap shrink-0 items-center justify-center rounded-lg text-text-faint hover:bg-danger/15 hover:text-danger disabled:opacity-50"
          >
            <IconTrash />
          </button>
        )}
      </div>

      <LinearProgress
        variant="determinate"
        value={node.totalFragments > 0 ? Math.min(1, node.usedFragments / node.totalFragments) * 100 : 0}
        className="mt-2"
        sx={{
          height: 8,
          borderRadius: '9999px',
          backgroundColor: 'var(--accent-dim)',
          '& .MuiLinearProgress-bar': {
            backgroundColor: 'var(--accent)',
            borderRadius: '9999px',
          },
        }}
      />

      <div className="mt-2 flex flex-wrap items-end gap-3">
        <label htmlFor={inputId} className="flex flex-col gap-1 text-xs text-text-faint">
          Level (0-30)
          <input
            id={inputId}
            type="number"
            inputMode="numeric"
            min={0}
            max={30}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onBlur={commit}
            className="min-h-tap w-20 rounded-lg border border-border-strong bg-bg-elevated px-2 text-sm text-text focus:border-accent focus:outline-none"
          />
        </label>
        <span aria-hidden="true" className="pb-2 text-text-faint">
          →
        </span>
        <label htmlFor={targetInputId} className="flex flex-col gap-1 text-xs text-text-faint">
          Goal (0-30)
          <input
            id={targetInputId}
            type="number"
            inputMode="numeric"
            min={0}
            max={30}
            value={targetDraft}
            onChange={(e) => setTargetDraft(e.target.value)}
            onBlur={commitTarget}
            className="min-h-tap w-20 rounded-lg border border-border-strong bg-bg-elevated px-2 text-sm text-text focus:border-accent focus:outline-none"
          />
        </label>
        <p className="text-xs text-text-muted">
          used {node.usedSolErda.toLocaleString()} <HexaCurrencyIcon currency="sol-erda" /> ·{' '}
          {node.usedFragments.toLocaleString()} <HexaCurrencyIcon currency="sol-erda-fragment" />
          {node.maxed ? (
            <span className="ml-1 font-semibold text-success">— goal reached</span>
          ) : (
            <>
              {' '}
              — {missingSolErda.toLocaleString()} <HexaCurrencyIcon currency="sol-erda" /> ·{' '}
              {missingFragments.toLocaleString()} <HexaCurrencyIcon currency="sol-erda-fragment" /> to goal
            </>
          )}
        </p>
      </div>

      {confirmingDelete && (
        <ConfirmDialog
          title={`Remove ${label}`}
          message={`${label} is level ${node.level} with progress recorded. Removing it deletes that progress. This cannot be undone.`}
          confirmLabel="Remove"
          danger
          pending={deleteNode.isPending}
          onCancel={() => setConfirmingDelete(false)}
          onConfirm={() =>
            deleteNode.mutate(
              { nodeType: node.nodeType, nodeIndex: node.nodeIndex },
              { onSuccess: () => setConfirmingDelete(false) },
            )
          }
        />
      )}
    </li>
  );
}

function HexaTypeGroup({
  characterId,
  nodeType,
  nodes,
  classSkillCatalog,
  catalogLoaded,
  onLevelChange,
  onTargetChange,
  onAdd,
  addPending,
}: {
  characterId: number;
  nodeType: string;
  nodes: HexaNodeDto[];
  /** This class's full `hexa_class_skill` catalog (all node types) — `HexaTypeGroup` filters it
   *  down to this group's own rows per node index. Empty for classes with no catalog data yet
   *  (unverified/not-yet-researched) or while the reference query is still loading. */
  classSkillCatalog: HexaClassSkillDto[];
  /** False while the reference query is still loading — gates the fallback "add node" affordance
   *  so it can't flash in before the catalog decides whether this class needs it. */
  catalogLoaded: boolean;
  onLevelChange: (nodeType: string, nodeIndex: number, level: number) => void;
  onTargetChange: (nodeType: string, nodeIndex: number, targetLevel: number) => void;
  onAdd: (nodeType: string, nodeIndex: number) => void;
  addPending: boolean;
}) {
  const flexible = FLEXIBLE_NODE_TYPES.has(nodeType);
  // Slots the class catalog defines for this type are always present server-side (never removable,
  // no "add node" flow). The add/remove fallback only remains for an uncataloged class — plus any
  // legacy stored node at an index outside the catalog, which stays individually removable.
  const catalogIndices = new Set(
    classSkillCatalog.filter((o) => o.nodeType === nodeType).map((o) => o.nodeIndex),
  );
  const usedIndices = new Set(nodes.map((n) => n.nodeIndex));
  let nextIndex = 0;
  while (usedIndices.has(nextIndex) && nextIndex < MAX_FLEXIBLE_NODES) {
    nextIndex += 1;
  }
  const canAdd = flexible && catalogLoaded && catalogIndices.size === 0 && nodes.length < MAX_FLEXIBLE_NODES;

  return (
    <div>
      <h3 className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-text-faint">
        <HexaTypeIcon nodeType={nodeType} />
        {NODE_TYPE_LABEL[nodeType]}
      </h3>
      {nodes.length === 0 && flexible && catalogLoaded && catalogIndices.size === 0 && (
        <p className="mt-1 text-xs text-text-faint">No nodes added yet.</p>
      )}
      {nodes.length > 0 && (
        <ul className="mt-2 grid grid-cols-1 gap-2 sm:grid-cols-2 xl:grid-cols-3">
          {nodes.map((node) => (
            <HexaNodeRow
              key={`${node.nodeType}-${node.nodeIndex}`}
              characterId={characterId}
              node={node}
              removable={flexible && !catalogIndices.has(node.nodeIndex)}
              onLevelChange={(level) => onLevelChange(node.nodeType, node.nodeIndex, level)}
              onTargetChange={(targetLevel) => onTargetChange(node.nodeType, node.nodeIndex, targetLevel)}
            />
          ))}
        </ul>
      )}
      {canAdd && (
        <button
          type="button"
          disabled={addPending}
          onClick={() => onAdd(nodeType, nextIndex)}
          className="mt-2 min-h-tap w-full rounded-lg border border-dashed border-border-strong text-sm font-semibold text-text-faint transition-colors hover:border-accent hover:text-accent disabled:opacity-50"
        >
          + Add {NODE_TYPE_LABEL[nodeType]} node ({nodes.length}/{MAX_FLEXIBLE_NODES})
        </button>
      )}
    </div>
  );
}

// -------------------------------------------------------------------------------------------
// HEXA Stat (REV22 Fix 2/2c — docs/specs/REV22-hexa-stat-and-merged-masteries.md)
// -------------------------------------------------------------------------------------------

const HEXA_STAT_ROMAN = ['I', 'II', 'III'] as const;

/**
 * One HEXA Stat node card. REV22 Fix 2c (2026-08-29) collapsed this from a 3-stat-line editor down
 * to the exact same level→goal idiom `HexaNodeRow` uses for every other node type: a `LinearProgress`
 * bar, a debounced Level (0-20) input, a debounced Goal (0-20) input, and a clear affordance —
 * `useUpsertHexaStatNode` mirrors `useUpsertHexaNode`'s partial-update convention (an omitted
 * `targetLevel` leaves the stored goal untouched). The Fix 2b cost line (unlock exact/struck-through
 * once paid, leveling an "≈"-marked estimate from `HexaStatCostModel`'s roll-cost curve) is
 * unchanged in shape, just no longer keyed off any per-line breakdown.
 */
function HexaStatCard({ characterId, statNode }: { characterId: number; statNode: HexaStatNodeDto }) {
  const upsert = useUpsertHexaStatNode(characterId);
  const deleteNode = useDeleteHexaStatNode(characterId);
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  const [levelDraft, setLevelDraft, commitLevel] = useDebouncedLevelInput(
    statNode.level,
    (level) => upsert.mutate({ nodeIndex: statNode.nodeIndex, level }),
    20,
  );
  const [targetDraft, setTargetDraft, commitTarget] = useDebouncedLevelInput(
    statNode.targetLevel,
    (targetLevel) => upsert.mutate({ nodeIndex: statNode.nodeIndex, level: statNode.level, targetLevel }),
    20,
  );

  const title = `HEXA Stat ${HEXA_STAT_ROMAN[statNode.nodeIndex]} — Lv ${statNode.unlockLevel}`;
  const levelInputId = `hexa-stat-level-${statNode.nodeIndex}`;
  const targetInputId = `hexa-stat-target-${statNode.nodeIndex}`;

  const requestRemove = () => {
    if (statNode.level > 0) {
      setConfirmingDelete(true);
    } else {
      deleteNode.mutate(statNode.nodeIndex);
    }
  };

  return (
    <li className="rounded-lg border border-border bg-bg-elevated-hover p-3">
      <div className="flex items-center justify-between gap-2">
        <p className="text-sm font-medium text-text">{title}</p>
        <button
          type="button"
          aria-label={`Remove ${title}`}
          disabled={deleteNode.isPending}
          onClick={requestRemove}
          className="flex min-h-tap min-w-tap shrink-0 items-center justify-center rounded-lg text-text-faint hover:bg-danger/15 hover:text-danger disabled:opacity-50"
        >
          <IconTrash />
        </button>
      </div>

      <LinearProgress
        variant="determinate"
        value={statNode.targetLevel > 0 ? Math.min(1, statNode.level / statNode.targetLevel) * 100 : 0}
        className="mt-2"
        sx={{
          height: 8,
          borderRadius: '9999px',
          backgroundColor: 'var(--accent-dim)',
          '& .MuiLinearProgress-bar': {
            backgroundColor: 'var(--accent)',
            borderRadius: '9999px',
          },
        }}
      />

      <div className="mt-2 flex flex-wrap items-end gap-3">
        <label htmlFor={levelInputId} className="flex flex-col gap-1 text-xs text-text-faint">
          Level (0-20)
          <input
            id={levelInputId}
            type="number"
            inputMode="numeric"
            min={0}
            max={20}
            value={levelDraft}
            onChange={(e) => setLevelDraft(e.target.value)}
            onBlur={commitLevel}
            className="min-h-tap w-20 rounded-lg border border-border-strong bg-bg-elevated px-2 text-sm text-text focus:border-accent focus:outline-none"
          />
        </label>
        <span aria-hidden="true" className="pb-2 text-text-faint">
          →
        </span>
        <label htmlFor={targetInputId} className="flex flex-col gap-1 text-xs text-text-faint">
          Goal (0-20)
          <input
            id={targetInputId}
            type="number"
            inputMode="numeric"
            min={0}
            max={20}
            value={targetDraft}
            onChange={(e) => setTargetDraft(e.target.value)}
            onBlur={commitTarget}
            className="min-h-tap w-20 rounded-lg border border-border-strong bg-bg-elevated px-2 text-sm text-text focus:border-accent focus:outline-none"
          />
        </label>
        {statNode.maxed && <span className="pb-2 text-xs font-semibold text-success">— goal reached</span>}
      </div>

      {/* REV22 Fix 2b: unlock is exact (struck through once paid); leveling is always an
          expected-value estimate (the "≈" here isn't conditional the way the goal-progress card's
          is — a HEXA Stat card's own to-goal figure is never anything but an estimate). */}
      <p className="mt-2 text-xs text-text-muted">
        <span className={statNode.unlocked ? 'text-text-faint line-through decoration-text-faint/60' : undefined}>
          unlock {statNode.unlockSolErda.toLocaleString()} <HexaCurrencyIcon currency="sol-erda" /> +{' '}
          {statNode.unlockFragments.toLocaleString()} <HexaCurrencyIcon currency="sol-erda-fragment" />
        </span>
        {' · '}
        <EstimatePrefix estimated />
        {statNode.estFragmentsToGoal.toLocaleString()} <HexaCurrencyIcon currency="sol-erda-fragment" /> to goal
      </p>

      {confirmingDelete && (
        <ConfirmDialog
          title={`Remove ${title}`}
          message={`${title} is level ${statNode.level} with progress recorded. Removing it deletes that progress. This cannot be undone.`}
          confirmLabel="Remove"
          danger
          pending={deleteNode.isPending}
          onCancel={() => setConfirmingDelete(false)}
          onConfirm={() => deleteNode.mutate(statNode.nodeIndex, { onSuccess: () => setConfirmingDelete(false) })}
        />
      )}
    </li>
  );
}

/**
 * A MUI `CircularProgress` ring with arbitrary centered content (the overall %, or a node's skill
 * icon). MUI has no track variant, so this is the library's own documented stacking recipe: a
 * static 100% ring in the theme's dim accent underneath, the real determinate value on top —
 * the same dim-track/bright-fill palette as the node cards' `LinearProgress`.
 *
 * Exported for `SharedProfile`'s roster cards, which wrap the same ring around the nav's hexagon
 * glyph to carry each character's overall HEXA % on the public page.
 */
export function RingProgress({
  value,
  size,
  thickness,
  color,
  children,
}: {
  value: number;
  size: number;
  thickness: number;
  color: string;
  children: ReactNode;
}) {
  return (
    <div className="relative inline-flex shrink-0">
      <CircularProgress
        variant="determinate"
        value={100}
        size={size}
        thickness={thickness}
        aria-hidden="true"
        sx={{ color: 'var(--accent-dim)' }}
      />
      <CircularProgress
        variant="determinate"
        value={Math.min(100, value)}
        size={size}
        thickness={thickness}
        sx={{
          color,
          position: 'absolute',
          left: 0,
          '& .MuiCircularProgress-circle': { strokeLinecap: 'round' },
        }}
      />
      <div className="absolute inset-0 flex items-center justify-center">{children}</div>
    </div>
  );
}

/** One "what's left" readout in the goal-progress card — same tiny-uppercase-label-over-number
 *  idiom as `HexaOverview`'s roster totals strip, so the two HEXA summary surfaces stay kin. */
function GoalStat({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <p className="font-display text-[0.625rem] font-semibold uppercase tracking-wider text-text-faint">{label}</p>
      <p className="mt-0.5 text-base font-semibold text-text">{children}</p>
    </div>
  );
}

/**
 * The goal-progress card shown above a character's node list. Deliberately NOT the stacked
 * used/total bar pair every other HEXA tracker renders: a donut ring carries the single overall
 * goal %, the stats spell out what's actually still needed (fragments, Sol Erda, nodes), and the
 * node strip shows the player's real matrix — one skill icon per node with its own tiny goal bar
 * (success-colored once that node hits its target, dimmed while untouched). `rollup` and every
 * per-node figure are goal-based (each node's totals are relative to its own `targetLevel`,
 * defaulting to 30) — see `lib/hexaOverview.ts`'s `hexaRollup`. REV22 Fix 2b folds HEXA Stat's cost
 * into `rollup` too (unlock exact, leveling an expected-value estimate) — `rollup.estimated` gates
 * the "≈" prefix on the two SE/fragment stats below; the node strip itself still only shows the
 * node-level tracker (HEXA Stat gets its own card group further down).
 */
function HexaProgressWidget({ rollup, nodes }: { rollup: HexaRollup; nodes: HexaNodeDto[] }) {
  const nodesAtGoal = nodes.filter((n) => n.maxed).length;
  const allDone = nodes.length > 0 && nodesAtGoal === nodes.length;
  const ringColor = allDone ? 'var(--success)' : 'var(--accent)';

  return (
    <div className="rounded-xl border border-border bg-bg-elevated p-4 sm:p-5">
      <div className="flex flex-wrap items-center gap-x-6 gap-y-4">
        <RingProgress value={rollup.overallPct} size={80} thickness={4} color={ringColor}>
          <span className="font-display text-lg font-semibold text-text">{rollup.overallPct}%</span>
        </RingProgress>

        <div className="flex flex-wrap items-center gap-x-6 gap-y-3">
          <GoalStat label="Fragments to goal">
            <EstimatePrefix estimated={rollup.estimated} />
            {rollup.missingFragments.toLocaleString()} <HexaCurrencyIcon currency="sol-erda-fragment" />
          </GoalStat>
          <GoalStat label="Sol Erda to goal">
            <EstimatePrefix estimated={rollup.estimated} />
            {rollup.missingSolErda.toLocaleString()} <HexaCurrencyIcon currency="sol-erda" />
          </GoalStat>
          <GoalStat label="Nodes at goal">
            {allDone ? (
              <span className="text-success">
                {nodesAtGoal}/{nodes.length} ✓
              </span>
            ) : (
              <>
                {nodesAtGoal}/{nodes.length}
              </>
            )}
          </GoalStat>
        </div>
      </div>

      <HexaNodeRingStrip nodes={nodes} className="mt-4" />
    </div>
  );
}

/**
 * The per-node goal-progress strip: one ringed skill icon per node, dimmed while untouched,
 * success-colored once the node hits its own target. Extracted from `HexaProgressWidget` and
 * exported so `HexaOverview`'s per-character rows can carry the same strip in place of the old
 * text chips.
 */
export function HexaNodeRingStrip({ nodes, className }: { nodes: HexaNodeDto[]; className?: string }) {
  return (
    <ul className={['flex flex-wrap gap-2', className].filter(Boolean).join(' ')} aria-label="Per-node goal progress">
      {nodes.map((node) => {
        const nodePct = node.targetLevel > 0 ? (node.level / node.targetLevel) * 100 : 0;
        return (
          <li
            key={`${node.nodeType}-${node.nodeIndex}`}
            aria-label={`${nodeLabel(node)} — ${node.level}/${node.targetLevel}`}
          >
            <HexaSkillTooltip node={node} label={nodeLabel(node)}>
              <RingProgress
                value={nodePct}
                size={40}
                thickness={3}
                color={node.maxed ? 'var(--success)' : 'var(--accent)'}
              >
                <span className={node.level === 0 ? 'opacity-40 grayscale' : ''}>
                  {/* REV22 Fix 1: common_core needs its per-index Sol Janus/Sol Hecate art, not
                      the single shared type icon `node.skillSlug` alone would fall back to. */}
                  <HexaTypeIcon nodeType={node.nodeType} skillSlug={fallbackIconSlug(node)} />
                </span>
              </RingProgress>
            </HexaSkillTooltip>
          </li>
        );
      })}
    </ul>
  );
}

/** Shared token-tinted defaults for every HEXA skeleton block (MUI's own gray reads wrong on the
 *  app's dark elevated surfaces). */
const SKELETON_SX = { bgcolor: 'var(--bg-elevated-hover)' } as const;

/** Loading placeholder mirroring `HexaSection`'s real shape: the goal-progress card (ring + stats
 *  + node strip) above a node-list card — so the page doesn't reflow when data lands. */
export function HexaSectionSkeleton() {
  return (
    <div className="space-y-4" aria-hidden="true">
      <div className="rounded-xl border border-border bg-bg-elevated p-4 sm:p-5">
        <div className="flex flex-wrap items-center gap-x-6 gap-y-4">
          <Skeleton variant="circular" width={80} height={80} sx={SKELETON_SX} />
          {[0, 1, 2].map((i) => (
            <div key={i}>
              <Skeleton variant="text" width={96} sx={{ ...SKELETON_SX, fontSize: '0.625rem' }} />
              <Skeleton variant="text" width={64} sx={{ ...SKELETON_SX, fontSize: '1rem' }} />
            </div>
          ))}
        </div>
        <div className="mt-4 flex flex-wrap gap-2">
          {Array.from({ length: 8 }, (_, i) => (
            <Skeleton key={i} variant="circular" width={40} height={40} sx={SKELETON_SX} />
          ))}
        </div>
      </div>
      <div className="rounded-xl border border-border bg-bg-elevated p-5">
        <Skeleton variant="text" width={120} sx={{ ...SKELETON_SX, fontSize: '1rem' }} />
        <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2 xl:grid-cols-3">
          {Array.from({ length: 6 }, (_, i) => (
            <Skeleton key={i} variant="rounded" height={112} sx={{ ...SKELETON_SX, borderRadius: '0.5rem' }} />
          ))}
        </div>
      </div>
    </div>
  );
}

export function HexaSection({ characterId, classSlug }: { characterId: number; classSlug: string }) {
  const hexaQuery = useCharacterHexa(characterId);
  const classSkillsQuery = useHexaClassSkills(classSlug);
  const upsert = useUpsertHexaNode(characterId);

  if (hexaQuery.isPending) {
    return <HexaSectionSkeleton />;
  }
  if (hexaQuery.isError) {
    return (
      <CollapsibleSection title="HEXA">
        <p role="alert" className="text-sm text-danger">
          Could not load HEXA nodes.
        </p>
      </CollapsibleSection>
    );
  }

  const nodes = hexaQuery.data?.nodes ?? [];
  const statNodes = hexaQuery.data?.statNodes ?? [];
  // REV20: the SE/fragment rollup moved to `lib/hexaOverview.ts` (shared with the new `/hexa`
  // Overview surface) — see that module's header comment. Goal-based: each node's own totals are
  // already relative to its `targetLevel`, not a hardcoded level 30. REV22 Fix 2b folds HEXA Stat's
  // (unlock-exact, leveling-estimated) cost in too — `rollup.estimated` flags that to the UI.
  const rollup = hexaRollup(nodes, statNodes);

  const byType = new Map<string, HexaNodeDto[]>();
  for (const type of NODE_TYPE_ORDER) {
    byType.set(type, []);
  }
  for (const node of nodes) {
    byType.get(node.nodeType)?.push(node);
  }
  for (const list of byType.values()) {
    list.sort((a, b) => a.nodeIndex - b.nodeIndex);
  }

  const classSkillCatalog = classSkillsQuery.data ?? [];

  return (
    <div className="space-y-4">
      {nodes.length > 0 && (
        <HexaProgressWidget rollup={rollup} nodes={NODE_TYPE_ORDER.flatMap((type) => byType.get(type) ?? [])} />
      )}

      <CollapsibleSection title="HEXA">
        <div className="space-y-4">
          {NODE_TYPE_ORDER.map((type) => (
            <HexaTypeGroup
              key={type}
              characterId={characterId}
              nodeType={type}
              nodes={byType.get(type) ?? []}
              classSkillCatalog={classSkillCatalog}
              catalogLoaded={classSkillsQuery.data !== undefined}
              onLevelChange={(nodeType, nodeIndex, level) => upsert.mutate({ nodeType, nodeIndex, level })}
              onTargetChange={(nodeType, nodeIndex, targetLevel) => {
                const level = byType.get(nodeType)?.find((n) => n.nodeIndex === nodeIndex)?.level ?? 0;
                upsert.mutate({ nodeType, nodeIndex, level, targetLevel });
              }}
              onAdd={(nodeType, nodeIndex) => upsert.mutate({ nodeType, nodeIndex, level: 0 })}
              addPending={upsert.isPending}
            />
          ))}

          <div>
            <h3 className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-text-faint">
              HEXA Stat
            </h3>
            <ul className="mt-2 grid grid-cols-1 gap-2 sm:grid-cols-2 xl:grid-cols-3">
              {statNodes.map((statNode) => (
                <HexaStatCard key={statNode.nodeIndex} characterId={characterId} statNode={statNode} />
              ))}
            </ul>
          </div>
        </div>
      </CollapsibleSection>
    </div>
  );
}
